import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import api, { extractValidationFieldErrors, getApiErrorMessage } from '../services/api';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import { formatJstDateTime } from '../utils/datetime';
import {
  BranchVerifyResponse,
  SokyuConfigRead,
  SokyuConfigUpdate,
  SokyuSyncStatusItem,
  StoreSokyuItem,
} from '../types';

type TabKey = 'configs' | 'status';

// 手数料負担区分は契約固定（企業負担）。編集させず表示だけ行う。
// 引き出し処理は app/services/sokyu/constants.py の SOKYU_FEE_SHARE_KBN_FIXED しか見ておらず、
// 画面から変更できると「設定したのに実挙動が変わらない」誤解を生むため（動作確認NG 5章5-2）。
const FEE_SHARE_FIXED_LABEL = '企業負担（固定）';

const mwOptions = [
  { value: '', label: '未設定' },
  { value: '0', label: '0: 同月' },
  { value: '1', label: '1: 翌月' },
];

const salaryPayMwOptions = [
  ...mwOptions,
  { value: '2', label: '2: 翌々月' },
];

const SokyuConfigPage: React.FC = () => {
  const { managementToken } = useManagementAuth();
  const [tab, setTab] = useState<TabKey>('configs');
  const [configs, setConfigs] = useState<SokyuConfigRead[]>([]);
  const [syncStatus, setSyncStatus] = useState<SokyuSyncStatusItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const [editTarget, setEditTarget] = useState<SokyuConfigRead | null>(null);
  const [editForm, setEditForm] = useState<SokyuConfigUpdate>({});
  const [saving, setSaving] = useState(false);
  const [enableError, setEnableError] = useState<{ missing_fields: string[] } | null>(null);
  // モーダル内の保存エラー（親画面ではなくダイアログ内に表示する）
  const [saveError, setSaveError] = useState('');
  const [saveErrorField, setSaveErrorField] = useState<string | null>(null);

  // 店舗別 事業所番号の編集
  const [storeEditTarget, setStoreEditTarget] = useState<
    { companyId: string; companyName: string; store: StoreSokyuItem } | null
  >(null);
  const [storeBranchValue, setStoreBranchValue] = useState('');
  const [storeSaving, setStoreSaving] = useState(false);
  const [storeSaveError, setStoreSaveError] = useState('');
  // 事業所番号の即給マスタ照合。マスタに無い値を保存すると、その店舗に所属する
  // ワーカー全員の口座登録・変更が「事業所番号：マスタに存在しない値です」で失敗する。
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<BranchVerifyResponse | null>(null);
  // 進行中の照合リクエストの識別子。遅延到着した応答を捨てるために使う。
  const verifyRequestRef = useRef<{ storeId: string; value: string } | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [configList, status] = await Promise.all([
        api.adminSokyu.listConfigs(),
        api.adminSokyu.getSyncStatus(),
      ]);
      setConfigs(configList);
      setSyncStatus(status);
    } catch (e: any) {
      setError(getApiErrorMessage(e, '取得に失敗しました'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const openEdit = (config: SokyuConfigRead) => {
    setEditTarget(config);
    setEditForm({
      sokyu_company_no: config.sokyu_company_no ?? null,
      sokyu_company_id: config.sokyu_company_id ?? null,
      sokyu_salary_pay_cycle_no: config.sokyu_salary_pay_cycle_no ?? null,
      sokyu_reception_start_day: config.sokyu_reception_start_day ?? null,
      sokyu_reception_end_mw: config.sokyu_reception_end_mw ?? null,
      sokyu_reception_end_day: config.sokyu_reception_end_day ?? null,
      sokyu_salary_pay_mw: config.sokyu_salary_pay_mw ?? null,
      sokyu_salary_pay_day: config.sokyu_salary_pay_day ?? null,
      // 手数料負担区分は編集対象外（サーバ側で常に企業負担へそろえる）
    });
    setEnableError(null);
    setSaveError('');
    setSaveErrorField(null);
  };

  const closeEdit = () => {
    setEditTarget(null);
    setEditForm({});
    setEnableError(null);
    setSaveError('');
    setSaveErrorField(null);
  };

  const saveConfig = async () => {
    if (!editTarget) return;
    setSaving(true);
    setSaveError('');
    setSaveErrorField(null);
    try {
      const updated = await api.adminSokyu.updateConfig(editTarget.company_id, editForm);
      setConfigs((prev) => prev.map((c) => (c.company_id === updated.company_id ? updated : c)));
      setInfo(`${updated.company_name} の即給連携設定を保存しました`);
      closeEdit();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.message) {
        setSaveError(detail.message);
        setSaveErrorField(detail.field ?? null);
      } else {
        setSaveError(
          (typeof detail === 'string' ? detail : null) || e.message || '保存に失敗しました',
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const openStoreEdit = (company: SokyuConfigRead, store: StoreSokyuItem) => {
    setStoreEditTarget({
      companyId: company.company_id,
      companyName: company.company_name,
      store,
    });
    setStoreBranchValue(store.sokyu_branch_no ?? '');
    setStoreSaveError('');
    setVerifyResult(null);
    // 進行中の照合を無効化する（遅延到着した結果を別店舗に表示させない・ボタンを解放する）
    verifyRequestRef.current = null;
    setVerifying(false);
  };

  const closeStoreEdit = () => {
    setStoreEditTarget(null);
    setStoreBranchValue('');
    setStoreSaveError('');
    setVerifyResult(null);
    // 進行中の照合を無効化する（遅延到着した結果を別店舗に表示させない・ボタンを解放する）
    verifyRequestRef.current = null;
    setVerifying(false);
  };

  const verifyStoreBranch = async () => {
    if (!storeEditTarget) return;
    const trimmed = storeBranchValue.trim();
    if (trimmed === '') return;
    if (!managementToken) {
      setStoreSaveError('即給との照合は運営アカウントでのログインが必要です');
      return;
    }
    // 即給APIは最大30秒かかる。応答待ちの間にダイアログを閉じて別店舗を開くと、
    // 遅れて届いた結果が別店舗の値の判定として表示されてしまうため、
    // 発行時の店舗＋値を覚えておき、一致しなければ結果を捨てる。
    const issuedFor = { storeId: storeEditTarget.store.store_id, value: trimmed };
    verifyRequestRef.current = issuedFor;
    const isLatest = () => verifyRequestRef.current === issuedFor;

    setVerifying(true);
    setVerifyResult(null);
    setStoreSaveError('');
    try {
      const result = await api.adminSokyu.verifyBranchNo(
        storeEditTarget.companyId, trimmed, managementToken,
      );
      if (isLatest()) setVerifyResult(result);
    } catch (e: any) {
      if (isLatest()) setStoreSaveError(getApiErrorMessage(e, '照合に失敗しました'));
    } finally {
      if (isLatest()) setVerifying(false);
    }
  };

  const saveStoreBranch = async () => {
    if (!storeEditTarget) return;
    setStoreSaving(true);
    setStoreSaveError('');
    try {
      const trimmed = storeBranchValue.trim();
      const updated = await api.adminSokyu.updateStoreBranch(
        storeEditTarget.companyId,
        storeEditTarget.store.store_id,
        { sokyu_branch_no: trimmed === '' ? null : trimmed },
      );
      setConfigs((prev) => prev.map((c) => (c.company_id === updated.company_id ? updated : c)));
      setInfo(
        `${updated.company_name} / ${storeEditTarget.store.store_name ?? ''} の事業所番号を保存しました`,
      );
      closeStoreEdit();
    } catch (e: any) {
      // 422（半角英数5桁でない等）は detail が配列なので共通ハンドラでは理由が落ちる。
      // 入力欄の指摘文へ変換して「なぜ保存できないか」を出す。
      const fieldErrors = extractValidationFieldErrors(e, { sokyu_branch_no: '事業所番号' });
      setStoreSaveError(
        fieldErrors.sokyu_branch_no || getApiErrorMessage(e, '保存に失敗しました'),
      );
    } finally {
      setStoreSaving(false);
    }
  };

  const toggleEnable = async (config: SokyuConfigRead, enable: boolean, force = false) => {
    setError('');
    setEnableError(null);
    try {
      const updated = enable
        ? await api.adminSokyu.enable(config.company_id, force)
        : await api.adminSokyu.disable(config.company_id);
      setConfigs((prev) => prev.map((c) => (c.company_id === updated.company_id ? updated : c)));
      setInfo(
        `${updated.company_name} を${updated.sokyu_enabled ? '有効化' : '無効化'}しました`,
      );
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.error_code === 'SOKYU_CONFIG_INCOMPLETE') {
        setEnableError({ missing_fields: detail.missing_fields || [] });
      } else {
        setError(detail || e.message || '操作に失敗しました');
      }
    }
  };

  const formatYen = (v: number) =>
    new Intl.NumberFormat('ja-JP', { style: 'currency', currency: 'JPY', minimumFractionDigits: 0 }).format(v);
  const formatDate = (v: string | null | undefined) => formatJstDateTime(v);

  const configComplete = useMemo(() => configs.filter((c) => c.is_configuration_complete).length, [configs]);
  const enabledCount = useMemo(() => configs.filter((c) => c.sokyu_enabled).length, [configs]);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          即給連携管理
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchAll} disabled={loading}>
          更新
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {info && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setInfo('')}>
          {info}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', gap: 4 }}>
            <Box>
              <Typography variant="caption" color="text.secondary">企業総数</Typography>
              <Typography variant="h6">{configs.length}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">設定完了</Typography>
              <Typography variant="h6">{configComplete}</Typography>
            </Box>
            <Box>
              <Typography variant="caption" color="text.secondary">有効化済み</Typography>
              <Typography variant="h6" color="success.main">{enabledCount}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab value="configs" label="連携設定" />
        <Tab value="status" label="同期状況" />
      </Tabs>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && tab === 'configs' && (
        <Box>
          {configs.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
              企業がありません
            </Typography>
          )}
          {configs.map((c) => (
            <Accordion key={c.company_id} disableGutters sx={{ mb: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 2 },
                    width: '100%',
                    flexWrap: 'wrap',
                    pr: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, flexGrow: 1, minWidth: 120 }}>
                    {c.company_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    企業番号: {c.sokyu_company_no ?? '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    店舗数: {c.stores.length}
                  </Typography>
                  <Chip
                    label={c.is_configuration_complete ? '設定完了' : '設定未完了'}
                    color={c.is_configuration_complete ? 'success' : 'warning'}
                    size="small"
                  />
                  <Chip
                    label={c.sokyu_enabled ? '有効' : '無効'}
                    color={c.sokyu_enabled ? 'success' : 'default'}
                    size="small"
                  />
                  {/* アコーディオンの開閉に伝播させない */}
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                    onClick={(e) => e.stopPropagation()}
                    onFocus={(e) => e.stopPropagation()}
                  >
                    <IconButton size="small" onClick={() => openEdit(c)} title="企業設定を編集">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <Button
                      size="small"
                      color={c.sokyu_enabled ? 'warning' : 'success'}
                      onClick={() => toggleEnable(c, !c.sokyu_enabled)}
                      disabled={!c.sokyu_enabled && !c.is_configuration_complete}
                    >
                      {c.sokyu_enabled ? '無効化' : '有効化'}
                    </Button>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'action.hover' }}>
                {/* 契約固定値。編集はできないが、契約説明・導入チェック時に画面で確認できるよう表示する。 */}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  手数料負担区分： <strong>{FEE_SHARE_FIXED_LABEL}</strong>
                  <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                    （振込手数料はクルーの受取額から差し引かれません）
                  </Typography>
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  店舗別 事業所番号
                </Typography>
                {c.stores.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    この企業に紐づく店舗がありません
                  </Typography>
                ) : (
                  <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
                    <Table size="small" sx={{ minWidth: 360 }}>
                      <TableHead>
                        <TableRow>
                          <TableCell>店舗名</TableCell>
                          <TableCell>事業所番号</TableCell>
                          <TableCell align="right">操作</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {c.stores.map((s) => (
                          <TableRow key={s.store_id}>
                            <TableCell>{s.store_name ?? '-'}</TableCell>
                            <TableCell>
                              {s.sokyu_branch_no ? (
                                s.sokyu_branch_no
                              ) : (
                                <Chip label="未設定" size="small" color="warning" variant="outlined" />
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <IconButton
                                size="small"
                                onClick={() => openStoreEdit(c, s)}
                                title="事業所番号を編集"
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {!loading && tab === 'status' && (
        <TableContainer component={Card} sx={{ overflowX: 'auto' }}>
          <Table sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow>
                <TableCell>企業名</TableCell>
                {/* 即給店舗単位化: 1行=1店舗。店舗名と実効利用可否を表示 */}
                <TableCell>店舗名</TableCell>
                <TableCell>企業番号</TableCell>
                <TableCell>即給利用</TableCell>
                <TableCell align="right">登録ユーザー数</TableCell>
                <TableCell align="right">ウォレット合計</TableCell>
                <TableCell>最終同期</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {syncStatus.map((s) => (
                // 店舗単位化で company_id は非ユニーク。複合キーで React key 衝突を防ぐ。
                <TableRow key={`${s.company_id}:${s.store_id ?? 'unassigned'}`}>
                  <TableCell>{s.company_name}</TableCell>
                  <TableCell>{s.store_name ?? '-'}</TableCell>
                  <TableCell>{s.sokyu_company_no ?? '-'}</TableCell>
                  <TableCell>
                    {/* 実効利用可否（企業 sokyu_enabled AND 事業所番号入力済み）。
                        BE が is_store_usable を返さない旧レスポンス互換で sokyu_enabled にフォールバック。 */}
                    <Chip
                      label={(s.is_store_usable ?? s.sokyu_enabled) ? '利用可' : '利用不可'}
                      color={(s.is_store_usable ?? s.sokyu_enabled) ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{s.registered_users_count}</TableCell>
                  <TableCell align="right">{formatYen(s.total_wallet_balance)}</TableCell>
                  <TableCell>{formatDate(s.last_wallet_synced_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* 企業設定 編集ダイアログ（事業所番号は店舗側で編集するため含めない） */}
      <Dialog open={!!editTarget} onClose={closeEdit} maxWidth="sm" fullWidth>
        <DialogTitle>
          即給連携設定: {editTarget?.company_name}
        </DialogTitle>
        <DialogContent>
          {enableError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              未入力の項目があります: {enableError.missing_fields.join(', ')}
            </Alert>
          )}
          {saveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => { setSaveError(''); setSaveErrorField(null); }}>
              {saveError}
            </Alert>
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="企業番号（4桁）"
              value={editForm.sokyu_company_no ?? ''}
              onChange={(e) => {
                setEditForm((f) => ({ ...f, sokyu_company_no: e.target.value || null }));
                if (saveErrorField === 'sokyu_company_no') {
                  setSaveError('');
                  setSaveErrorField(null);
                }
              }}
              inputProps={{ maxLength: 4 }}
              error={saveErrorField === 'sokyu_company_no'}
              fullWidth
            />
            <TextField
              label="企業ID（3桁）"
              value={editForm.sokyu_company_id ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, sokyu_company_id: e.target.value || null }))}
              inputProps={{ maxLength: 3 }}
              fullWidth
            />
            <TextField
              label="給与支払サイクル番号（2桁）"
              value={editForm.sokyu_salary_pay_cycle_no ?? ''}
              onChange={(e) => setEditForm((f) => ({ ...f, sokyu_salary_pay_cycle_no: e.target.value || null }))}
              inputProps={{ maxLength: 2 }}
              fullWidth
            />
            {/* 前払受付期間の3項目は即給側の契約内容を記録するだけの控え。
                実際の受付可否は即給API側で判定されるため、Re:Crew の動作には影響しない。 */}
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
              即給側契約値の控え（Re:Crew の動作には使用しません）
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                label="前払受付開始日"
                type="number"
                value={editForm.sokyu_reception_start_day ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, sokyu_reception_start_day: e.target.value ? Number(e.target.value) : null }))}
                inputProps={{ min: 1, max: 99 }}
                fullWidth
                helperText="1〜28 or 99(月末)"
              />
              <TextField
                select
                label="受付終了月週"
                value={editForm.sokyu_reception_end_mw ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, sokyu_reception_end_mw: e.target.value || null }))}
                fullWidth
              >
                {mwOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="受付終了日"
                type="number"
                value={editForm.sokyu_reception_end_day ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, sokyu_reception_end_day: e.target.value ? Number(e.target.value) : null }))}
                inputProps={{ min: 1, max: 99 }}
                fullWidth
              />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
              <TextField
                select
                label="給与支払月週"
                value={editForm.sokyu_salary_pay_mw ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, sokyu_salary_pay_mw: e.target.value || null }))}
                fullWidth
              >
                {salaryPayMwOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="給与支払日"
                type="number"
                value={editForm.sokyu_salary_pay_day ?? ''}
                onChange={(e) => setEditForm((f) => ({ ...f, sokyu_salary_pay_day: e.target.value ? Number(e.target.value) : null }))}
                inputProps={{ min: 1, max: 99 }}
                fullWidth
              />
            </Box>
            {/* 手数料負担区分は契約固定のため入力欄を置かない（表示のみ）。 */}
            <Typography variant="body2" color="text.secondary">
              手数料負担区分： <strong>{FEE_SHARE_FIXED_LABEL}</strong>
              <br />
              振込手数料はクルーの受取額から差し引かれません。契約固定のため変更できません。
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEdit} disabled={saving}>キャンセル</Button>
          <Button onClick={saveConfig} disabled={saving} variant="contained">
            {saving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 店舗別 事業所番号 編集ダイアログ */}
      <Dialog open={!!storeEditTarget} onClose={closeStoreEdit} maxWidth="xs" fullWidth>
        <DialogTitle>事業所番号の編集</DialogTitle>
        <DialogContent>
          {storeEditTarget && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {storeEditTarget.companyName} ／ {storeEditTarget.store.store_name ?? '-'}
            </Typography>
          )}
          {storeSaveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setStoreSaveError('')}>
              {storeSaveError}
            </Alert>
          )}
          <TextField
            label="事業所番号（半角英数・最大5桁）"
            value={storeBranchValue}
            onChange={(e) => {
              setStoreBranchValue(e.target.value);
              setVerifyResult(null);
            }}
            inputProps={{ maxLength: 5 }}
            helperText="空にすると未設定（登録時は企業値へフォールバック）"
            fullWidth
            autoFocus
            sx={{ mt: 1 }}
          />
          {verifyResult && (
            <Alert
              severity={
                verifyResult.is_valid
                  ? 'success'
                  : verifyResult.status === 'unreachable'
                    ? 'info'
                    : 'error'
              }
              sx={{ mt: 2 }}
            >
              {verifyResult.message}
              {verifyResult.raw_error && (
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  即給からの応答: {verifyResult.raw_error}
                </Typography>
              )}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStoreEdit} disabled={storeSaving || verifying}>キャンセル</Button>
          {/* 保存前に即給マスタと突き合わせる。誤った値のまま保存されると、
              その店舗のワーカー全員が口座登録・変更できなくなる。 */}
          <Button
            onClick={verifyStoreBranch}
            disabled={storeSaving || verifying || storeBranchValue.trim() === ''}
          >
            {verifying ? '照合中...' : '即給と照合'}
          </Button>
          <Button onClick={saveStoreBranch} disabled={storeSaving || verifying} variant="contained">
            {storeSaving ? '保存中...' : '保存'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SokyuConfigPage;
